import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useCollectionData,
  useDocumentData,
} from "react-firebase-hooks/firestore";
import { doc, query, where } from "firebase/firestore";
import { Text } from "~/components/text";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/button";
import { Badge } from "~/components/ui/badge";
import { getPipelineReportCollection } from "~/integrations/firebase/collections";
import { useOnixChunks } from "~/hooks/use-onix-chunks";
import { usePagination } from "~/hooks/use-pagination";
import { useOnixImportReportExport } from "~/hooks/use-onix-import-report-export";
import { OrganisationOnixUploadFileReportDialogTable } from "./organisation-onix-upload-file-report-dialog-table";
import {
  getProductPublishedAt,
  getProductAuthors,
  getProductGroupDescription,
  getProductsImportCollection,
  getProductTitle,
} from "~/integrations/firebase/collections/products-import";
import { useErrorToast } from "~/hooks/use-error-toast";

interface ReportProduct {
  isbn: string;
  title?: string;
  author?: string[];
  chunkId: string;
  productGroupDescription?: string;
  publishedAt?: string;
  failed?: boolean;
  chunkFileName: string;
  errors: string[];
}

export const OrganisationOnixUploadFileReportDialog = ({
  open,
  onClose,
  reportId,
}: {
  open: boolean;
  reportId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["settings"]);

  const [reportProducts, setReportProducts] = useState<ReportProduct[]>([]);

  const {
    isDownloadReady,
    isActionLoading,
    showActionSkeleton,
    schedule,
    download,
    reset: resetExport,
  } = useOnixImportReportExport({ reportId, open });

  const { reset } = usePagination({
    prefix: "report",
  });

  const [report, isLoadingReport] = useDocumentData(
    doc(getPipelineReportCollection(), reportId),
  );

  const {
    chunks,
    loading: isLoadingChunks,
    itemsCount,
    error: chunksError,
  } = useOnixChunks({
    reportId,
    enabled: open,
  });

  const handleClose = () => {
    reset();
    resetExport();
    setReportProducts([]);
    onClose();
  };

  const chunkIds = useMemo(() => chunks?.map((c) => c.id) || [], [chunks]);

  const [productImports, isLoadingProductImports, productsImportError] =
    useCollectionData(
      chunkIds.length > 0
        ? query(
            getProductsImportCollection(),
            where("importData.chunkId", "in", chunkIds),
          )
        : undefined,
    );

  useErrorToast(productsImportError);

  useEffect(() => {
    if (!chunks || chunks.length === 0) {
      return;
    }

    // rebuilds report rows from the async chunk and product-import snapshots whenever either external source resolves
    setReportProducts(
      chunks.map((chunk) => {
        const product = productImports?.find(
          (p) => p?.importData.chunkId === chunk.id,
        );
        const productGroupDescription =
          getProductGroupDescription(product) || "";
        const title = getProductTitle(product);
        const author = getProductAuthors(product);
        const publishedAt = getProductPublishedAt(product);
        const validationErrors = chunk.errors || [];
        const parsingErrors = chunk.errorMessage
          ? [`There is unknown error in the product. errorId: ${chunk.id}`]
          : [];

        return {
          chunkId: chunk.id,
          isbn: chunk.isbn || "",
          failed: product ? (chunk.failed || []).length > 0 : true,
          publishedAt,
          errors:
            validationErrors.length > 0 ? validationErrors : parsingErrors,
          chunkFileName: chunk.fileName,
          productGroupDescription,
          title,
          author,
        };
      }),
    );
  }, [chunks, productImports]);

  const loading = isLoadingReport || isLoadingChunks || isLoadingProductImports;
  const failed = report?.productsFailed || 0;
  const succeeded = report?.productsSucceeded || 0;
  const total = report?.totalFiles || 0;
  const isActionLoadingMerged = useMemo(
    () => loading || isActionLoading,
    [loading, isActionLoading],
  );

  const actionIcon = useMemo(() => {
    if (isActionLoading) return "loader-circle";
    if (isDownloadReady) return "download";
    return "upload";
  }, [isActionLoading, isDownloadReady]);

  const actionLabel = useMemo(() => {
    if (isActionLoading)
      return t("settings:organisation.onix.report.dialog.generating");
    if (isDownloadReady)
      return t("settings:organisation.onix.report.dialog.download");
    return t("settings:organisation.onix.report.dialog.export");
  }, [isActionLoading, isDownloadReady, t]);

  const handleExport = async () => {
    await schedule();
  };

  const handleDownloadReport = async () => {
    await download();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="overflow-visible min-w-[90%] h-[70vh] flex flex-col">
        <DialogHeader className="items-start space-y-0 my-0 mb-8">
          <DialogTitle className="mb-3 flex items-center">
            {t("settings:organisation.onix.report.dialog.title")}
          </DialogTitle>
          <DialogDescription className="text-left" asChild>
            <div>
              <Text as="span" variant="subtle" className="mr-2 uppercase">
                {t("settings:organisation.onix.report.dialog.subtitle")}
              </Text>
              {total ? (
                <Text as="span" variant="muted">
                  {t("settings:organisation.onix.misc.books", { count: total })}
                </Text>
              ) : null}
              {failed ? (
                <>
                  <Text as="span" variant="subtle" className="mx-2">
                    |
                  </Text>
                  <Text as="span" variant="muted">
                    {t("settings:organisation.onix.misc.books", {
                      count: failed,
                    })}
                  </Text>
                  <Badge variant="error" className="ml-2">
                    {t("settings:organisation.onix.misc.badge.failed")}
                  </Badge>
                </>
              ) : null}
              {succeeded ? (
                <>
                  <Text as="span" variant="subtle" className="mx-2">
                    |
                  </Text>
                  <Text as="span" variant="muted">
                    {t("settings:organisation.onix.misc.books", {
                      count: succeeded,
                    })}
                  </Text>
                  <Badge variant="success" className="ml-2">
                    {t("settings:organisation.onix.misc.badge.success")}
                  </Badge>
                </>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {chunksError && (
            <div className="flex flex-col items-center justify-center h-full bg-system-error/5 rounded-lg">
              <Text variant="error" className="text-center">
                {t("general:errors.generic")}
              </Text>
            </div>
          )}
          {!chunksError && (
            <OrganisationOnixUploadFileReportDialogTable
              products={reportProducts}
              loading={loading}
              itemsCount={itemsCount}
            />
          )}
        </div>

        <DialogFooter className="flex justify-between gap-2 items-center">
          <div className="flex justify-end gap-2 ml-auto">
            <Button
              label={t("settings:organisation.onix.report.dialog.cancel")}
              onClick={handleClose}
              variant="tertiary"
              size="small"
            />
            {showActionSkeleton ? (
              <div className="h-8 w-24 m-1 items-center justify-center rounded-md bg-secondary-100 animate-pulse" />
            ) : (
              <Button
                label={actionLabel}
                variant={isDownloadReady ? "success" : "primary"}
                size="small"
                iconClassName={isActionLoading ? "animate-spin" : ""}
                icon={actionIcon}
                onClick={isDownloadReady ? handleDownloadReport : handleExport}
                disabled={isActionLoadingMerged}
              />
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
